// Coilmind subscription plans shown on the account page.
//
// Each tier is its OWN PayPro product (no shared product + option group):
//   Starter internal 901 → PayPro 135138
//   Basic   internal 902 → PayPro 135139  (recommended)
//   Premium internal 903 → PayPro 135140
// Checkout posts orders/new with items:[<internal productId>]; the backend
// builds products[1][id]=<paypro id>. After purchase the license from
// GET /soft/license (licenseType + maxDevices) is authoritative.

export const CURRENCY = '€';
export const PERIOD = 'month';
export const TRIAL_DAYS = 14; // trial applies to whichever plan is chosen

// ── Billing cycle ───────────────────────────────────────────────────────────
// FALLBACK ONLY. Real prices come from GET /products/plans (see usePlans.js);
// these values exist so the page renders something sane if that call fails, and
// they are not the source of truth. Yearly = ten months, i.e. two months free.
export const YEARLY_MONTHS_CHARGED = 10;
export const fallbackYearlyPrice = (plan) => plan.price * YEARLY_MONTHS_CHARGED;

/**
 * Billing cycles. Monthly and yearly are both permanent parts of the product:
 * the licence carries `billingCycle`, GET /products/plans publishes a row per
 * (tier, cycle) with real PayPro product ids, and change-tier accepts a `cycle`.
 *
 * The transition matrix is the server's, and the UI follows it rather than
 * inventing its own:
 *   tier ↑, same cycle      → now, prorated charge
 *   tier ↓, same cycle      → next cycle, no money
 *   cycle change, same tier → next cycle, no money (one ChangeProduct; the
 *                             annual price lands on the normal renewal date)
 *   tier AND cycle at once  → refused, 400 CYCLE_AND_TIER_CHANGE_UNSUPPORTED
 *
 * That last row is why the account page offers the tier list and the cycle
 * switch as two separate actions instead of a six-way matrix of buttons.
 */
export const CYCLE_MONTH = 'month';
export const CYCLE_YEAR = 'year';

export const CYCLE_LABEL = { [CYCLE_MONTH]: 'Monthly', [CYCLE_YEAR]: 'Yearly' };
export const CYCLE_PER = { [CYCLE_MONTH]: 'month', [CYCLE_YEAR]: 'year' };
export const CYCLE_SHORT = { [CYCLE_MONTH]: '/mo', [CYCLE_YEAR]: '/yr' };

// The vocabulary the backend, GET /products/plans and this catalogue all share
// is 'month' / 'year'; 'yearly' and 'annual' are accepted because URLs and older
// payloads use them. Anything else collapses to monthly — the cycle decides what
// a customer is charged, so an unrecognised value must never read as "yearly".
export const normalizeCycle = (cycle) => (
  [CYCLE_YEAR, 'yearly', 'annual'].includes(cycle) ? CYCLE_YEAR : CYCLE_MONTH
);

// The licence's own cycle, as the backend reports it. A trial has none — it is
// not billed on a cycle — and answers monthly, which is what its (absent) price
// would be rendered as anyway.
export const cycleForLicense = (license) => normalizeCycle(license?.billingCycle);

// ── Price accessors ─────────────────────────────────────────────────────────
// A plan that has been through mergePlans() carries `pricing[cycle]` from the
// server; one that hasn't falls back to the catalogue numbers above.
const rateFor = (plan, cycle) => plan?.pricing?.[cycle === CYCLE_YEAR ? CYCLE_YEAR : CYCLE_MONTH];

export const priceFor = (plan, cycle) => {
  const rate = rateFor(plan, cycle);
  if (rate) return rate.price;
  return cycle === CYCLE_YEAR ? fallbackYearlyPrice(plan) : plan.price;
};

// Which PayPro product a checkout must use. Each (tier, cycle) pair is its own
// product — that separation is what makes ChangeProduct-based upgrades possible.
// Taken from the server response so the id can never drift from the database.
export const productIdFor = (plan, cycle) => {
  const rate = rateFor(plan, normalizeCycle(cycle));
  if (rate) return rate.productId;
  return normalizeCycle(cycle) === CYCLE_YEAR ? plan.yearlyProductId : plan.productId;
};

// Promo display only. The server decides whether something is on sale and by
// how much; the frontend never derives a discount itself.
export const saleFor = (plan, cycle) => {
  const rate = rateFor(plan, cycle);
  if (!rate?.isSale || !rate?.priceBeforeDiscount) return null;
  return { was: rate.priceBeforeDiscount, percent: rate.discountPercent };
};

// What the yearly cycle saves, as computed by the server from the same two
// prices the customer is looking at.
export const yearlySaving = (plan) => {
  const rate = rateFor(plan, CYCLE_YEAR);
  if (rate?.savingAmountVsMonthly != null) return rate.savingAmountVsMonthly;
  return plan.price * (12 - YEARLY_MONTHS_CHARGED);
};

export const yearlySavingPercent = (plan) => (
  rateFor(plan, CYCLE_YEAR)?.savingPercentVsMonthly ?? null
);

export const formatPrice = (plan, cycle) => `${CURRENCY}${priceFor(plan, cycle).toLocaleString('en-US')}/${CYCLE_PER[normalizeCycle(cycle)]}`;

// ── TODO(pricing): confirm final numbers ────────────────────────────────────
// Every tier number a buyer sees lives here in one place, so finalizing pricing
// is a one-file edit. All PLACEHOLDERS pending the owner's decision:
//   price     — FALLBACK €/month. The live price comes from GET /products/plans;
//               this is what renders if that call fails.
//   requests  — soft WEEKLY cap (rolling 7-day; matches the internal weekly
//               budget). NOT shown to buyers — see tasksPerMonth.
//   tasksPerMonth — what every buyer-facing surface renders: "~N tasks / month".
//               A month reads like the allowance it actually is (~20/week sounds
//               smaller than the same ~85/month), and "task" is unambiguous where
//               "request" left people wondering whether each chat message counts.
//               Derived from `requests` × 52 ÷ 12, rounded to a friendly figure —
//               keep the two in step when the weekly budget changes. The rolling
//               7-day window is disclosed in PRICING_NOTE (content.js) so the
//               monthly headline is never mistaken for a burst allowance.
//   maxDevices— hard device allowance (also authoritative on the license)
//   reqPerMin — throughput / speed, rendered as "N req/min"
// Tiers differ ONLY by volume (requests), speed (reqPerMin) and devices — the
// underlying AI is uniform across plans, so never surface per-tier model names.
// productId is the internal products-table id — each tier is a separate PayPro
// product (see header), sent as items:[productId] to orders/new on checkout.
export const COILMIND_PLANS = [
  {
    key: 'starter',
    label: 'Starter',
    name: 'Coilmind Starter',
    productId: 901, // internal → PayPro 135138
    yearlyProductId: 911, // FALLBACK yearly product; the live id comes from /products/plans
    price: 29, // TODO(pricing)
    requests: 20, // TODO(pricing) — weekly cap
    tasksPerMonth: 85, // 20 × 52 ÷ 12 ≈ 87
    maxDevices: 2, // TODO(pricing)
    reqPerMin: 10, // TODO(pricing)
    bestFor: 'Occasional tasks',
  },
  {
    key: 'basic',
    label: 'Basic',
    name: 'Coilmind Basic',
    productId: 902, // internal → PayPro 135139
    yearlyProductId: 912, // FALLBACK yearly product; the live id comes from /products/plans
    price: 59, // TODO(pricing)
    requests: 55, // TODO(pricing) — weekly cap
    tasksPerMonth: 240, // 55 × 52 ÷ 12 ≈ 238
    maxDevices: 4, // TODO(pricing)
    reqPerMin: 15, // TODO(pricing)
    bestFor: 'Regular work',
    recommended: true, // pre-selected anchor + highlighted card
  },
  {
    key: 'premium',
    label: 'Premium',
    name: 'Coilmind Premium',
    productId: 903, // internal → PayPro 135140
    yearlyProductId: 913, // FALLBACK yearly product; the live id comes from /products/plans
    price: 149, // TODO(pricing)
    requests: 230, // TODO(pricing) — weekly cap
    tasksPerMonth: 1000, // 230 × 52 ÷ 12 ≈ 997
    maxDevices: 10, // TODO(pricing)
    reqPerMin: 25, // TODO(pricing)
    bestFor: 'Heavy / power use',
  },
];

/**
 * Fold the server's (tier, cycle) rows onto the static catalogue. The catalogue
 * keeps what is not a price — limits, devices, speed, copy, order, the
 * recommended flag — and the server supplies everything that is.
 */
export const mergePlans = (serverPlans) => {
  if (!Array.isArray(serverPlans) || serverPlans.length === 0) return COILMIND_PLANS;
  return COILMIND_PLANS.map((plan) => {
    const rows = serverPlans.filter((row) => row.tier === plan.key);
    if (rows.length === 0) return plan;
    const pricing = {};
    rows.forEach((row) => {
      pricing[row.cycle] = {
        price: Number(row.price),
        productId: row.productId,
        priceBeforeDiscount: row.priceBeforeDiscount ?? null,
        discountPercent: row.discountPercent ?? null,
        isSale: Boolean(row.isSale),
        pricePerMonth: row.pricePerMonth ?? null,
        savingPercentVsMonthly: row.savingPercentVsMonthly ?? null,
        savingAmountVsMonthly: row.savingAmountVsMonthly ?? null,
      };
    });
    return { ...plan, pricing };
  });
};

// Basic is the recommended anchor — pre-selected on every offer surface.
export const DEFAULT_PLAN_KEY = 'basic';

// Terminology-aware translation coverage (matches the Landing.jsx language list:
// EN · DE · IT · ES · FR · RU · ZH). Flags are unicode regional-indicator pairs.
export const LANGUAGES = [
  { code: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'DE', name: 'German', flag: '🇩🇪' },
  { code: 'IT', name: 'Italian', flag: '🇮🇹' },
  { code: 'ES', name: 'Spanish', flag: '🇪🇸' },
  { code: 'FR', name: 'French', flag: '🇫🇷' },
  { code: 'RU', name: 'Russian', flag: '🇷🇺' },
  { code: 'ZH', name: 'Chinese', flag: '🇨🇳' },
];

// Value props shared by every plan — lifted from the Coilmind landing so the
// account page argues the same product. (owner: verify each still holds.)
export const FEATURES = [
  'Works inside your live TIA project via the Openness API — not a chat window you paste code into',
  'Writes, imports and compiles real LAD / SCL — every change gated on a clean build',
  'Turns unreadable legacy STL into SCL, proven equivalent by test',
  'Terminology-aware translation across 7 languages',
  'Offline local model — air-gap ready; your code never leaves the PC',
  'AI included — no tokens to buy; your own key or a local model is optional',
];

// Resolve which plan a license belongs to. licenseType is authoritative when it
// matches a plan key; otherwise fall back to the device allowance (each tier
// has a distinct one). Null when nothing matches (e.g. legacy licenses).
export const planForLicense = (license) => {
  if (!license) return null;
  return COILMIND_PLANS.find((p) => p.key === license.licenseType)
    || COILMIND_PLANS.find((p) => p.maxDevices === license.maxDevices)
    || null;
};

export const RETENTION_OFFER = {
  percent: 30,
  months: 3,
};

// PayPro customer portal login (secondary/manual path). The primary path for
// card updates is the emailed one-time login link
// (POST /api/soft/subscription/payment-link — PayPro sends the email).
export const PAYPRO_CUSTOMER_PORTAL = 'https://cc.payproglobal.com/Customer/Account/Login';
